import { useState } from 'react';
import { useIntl } from 'react-intl';
import { EmptyState, IconTooltip, Tag, TaptapList } from 'sonata-react-components';
import { DOOH_REPORT_OTA_DISPLAY_CAP } from 'sonata-static-storage/lib/entities/doohReport/doohReport.constants';

import { HoverTooltip } from '@/components/common/HoverTooltip/HoverTooltip';
import { useDoohReportFormatters } from '@/components/doohReport/common/hooks/useDoohReportFormatters';
import OtaInfoModal from '@/components/doohReport/common/OtaInfoModal/OtaInfoModal';
import SectionHeader from '@/components/doohReport/common/SectionHeader/SectionHeader';
import EventActions from './EventActions';
import EventAudienceModal from './EventAudienceModal/EventAudienceModal';
import EventCategoryIcon from './EventCategoryIcon/EventCategoryIcon';
import { findHighestImpactEvent } from './eventsOta.helpers';
import HighestOtaBanner from './HighestOtaBanner';
import OtaReasons from './OtaReasons';

import type { DoohReportEvent } from 'sonata-static-storage/lib/entities/doohReport/doohReport.types';

import './contextEvents.scss';

/** Only the first events are listed (they come sorted by estimated audience). */
const VISIBLE_EVENTS = 5;

type Props = {
  events: DoohReportEvent[];
  sectionNumber: string;
  /** On-target share of an untargeted plan with the same spend (0-100). */
  baselineOnTargetPct: number | null;
  /** Campaign OTA. */
  campaignOta: number | null;
};

/**
 * "Context & Events" section of the DOOH report: the top real-world events near the
 * circuit, their event lift and their impact on the campaign, with the event that added the most
 * target people highlighted.
 */
export default function ContextEvents({ events, sectionNumber, baselineOnTargetPct, campaignOta }: Readonly<Props>) {
  const { formatMessage } = useIntl();
  const { formatCount, formatOta, formatShortDate } = useDoohReportFormatters();
  const [selectedEvent, setSelectedEvent] = useState<DoohReportEvent | null>(null);
  const [isOtaInfoOpen, setIsOtaInfoOpen] = useState(false);

  const header = (
    <SectionHeader
      description={formatMessage({
        defaultMessage: 'Real-World impact factors.',
        id: 'doohReport.contextEvents.description'
      })}
      number={sectionNumber}
      title={formatMessage({ defaultMessage: 'Context & Events.', id: 'doohReport.contextEvents.title' })}
      tooltip={formatMessage({
        defaultMessage:
          'The geo-context behind delivery — real-world events near the circuit and their estimated impact on how it delivered.',
        id: 'doohReport.contextEvents.tooltip'
      })}
    />
  );

  if (events.length === 0) {
    return (
      <section className="contextEvents">
        {header}
        <div className="contextEvents__card">
          <EmptyState
            description={formatMessage({
              defaultMessage: 'No events were found near this circuit for the current flight.',
              id: 'doohReport.contextEvents.emptyState.description'
            })}
            icon="fa-regular fa-calendar"
            id="contextEvents-emptyState"
            show
            title={formatMessage({
              defaultMessage: 'No nearby events yet',
              id: 'doohReport.contextEvents.emptyState.title'
            })}
          />
        </div>
      </section>
    );
  }

  const untitled = formatMessage({ defaultMessage: 'Untitled event', id: 'doohReport.contextEvents.untitled' });
  const visibleEvents = events.slice(0, VISIBLE_EVENTS);
  const highestImpactEvent = findHighestImpactEvent(visibleEvents);
  const eventLiftColumnTooltip = formatMessage(
    {
      defaultMessage:
        'Event lift · how concentrated your target audience was on the screens near the event while it took place, against an Untargeted plan (same spend). It measures a single moment, not the efficiency of your plan (that is the campaign OTA, {ota}). Above x{cap} it reads "> x{cap}".',
      id: 'doohReport.contextEvents.column.eventLift.tooltip'
    },
    { cap: DOOH_REPORT_OTA_DISPLAY_CAP, ota: formatOta(campaignOta) }
  );

  return (
    <section className="contextEvents">
      {header}
      <div className="contextEvents__card">
        <div className="contextEvents__cardHeader">
          <span className="contextEvents__cardTitle">
            {formatMessage({
              defaultMessage: 'Top events near the circuit and how they lifted delivery.',
              id: 'doohReport.contextEvents.cardTitle'
            })}
          </span>
          {highestImpactEvent && (
            <HighestOtaBanner
              campaignOta={campaignOta}
              event={highestImpactEvent}
              eventName={highestImpactEvent.name ?? untitled}
            />
          )}
        </div>
        <TaptapList<DoohReportEvent> className="contextEvents__list" data={visibleEvents} id="contextEvents-list">
          <TaptapList.ColumnsBar>
            <TaptapList.ColumnsBar.Column
              className="contextEvents__colEvent"
              description={formatMessage({ defaultMessage: 'Event', id: 'doohReport.contextEvents.column.event' })}
              id="event"
            />
            <TaptapList.ColumnsBar.Column
              className="contextEvents__colDates"
              description={formatMessage({ defaultMessage: 'Dates', id: 'doohReport.contextEvents.column.dates' })}
              id="dates"
            />
            {/* `Column` only takes a text description, and its own `tooltipText` renders a click
                tooltip under the header: the event lift header is built here to carry a hover ⓘ instead. */}
            <div className="taptapListColumnsBarColumn contextEvents__listColumnsBarColumn contextEvents__colOta">
              <div className="taptapListColumnsBarColumn__description contextEvents__headerWithInfo">
                <span className="taptapListColumnsBarColumn__descriptionText">
                  {formatMessage({ defaultMessage: 'Event lift', id: 'doohReport.contextEvents.column.eventLift' })}
                </span>
                <IconTooltip
                  icon="fa-regular fa-circle-info"
                  iconColor="var(--color-sonata-primary-500, #7B9FE1) !important"
                  place="top"
                  portal
                >
                  {eventLiftColumnTooltip}
                </IconTooltip>
              </div>
            </div>
            <TaptapList.ColumnsBar.Column
              className="contextEvents__colAudience"
              description={formatMessage({
                defaultMessage: 'Est. audience',
                id: 'doohReport.contextEvents.column.estimatedAudience'
              })}
              id="estimatedAudience"
            />
            <TaptapList.ColumnsBar.Column
              className="contextEvents__colReasons"
              description={formatMessage({
                defaultMessage: 'Event impact',
                id: 'doohReport.contextEvents.column.eventImpact'
              })}
              id="otaReasons"
            />
          </TaptapList.ColumnsBar>
          <TaptapList.RowsList<DoohReportEvent>>
            {itemsList =>
              itemsList.map(event => {
                const eventName = event.name ?? untitled;
                return (
                  <TaptapList.RowsList.Row id={event.id} key={event.id}>
                    <div className="contextEvents__row">
                      <div className="contextEvents__colEvent">
                        <EventCategoryIcon category={event.category} />
                        <div className="contextEvents__eventText">
                          <span className="contextEvents__eventNameRow">
                            <HoverTooltip content={eventName}>
                              <span className="contextEvents__eventName">{eventName}</span>
                            </HoverTooltip>
                            {event.id === highestImpactEvent?.id && (
                              <Tag
                                icon={{ position: 'left', webFont: 'fa-regular fa-circle-check' }}
                                id={`contextEvents-highestImpact-${event.id}`}
                                status="ok"
                                tiny
                              >
                                {formatMessage({
                                  defaultMessage: 'Highest impact',
                                  id: 'doohReport.contextEvents.highestImpact'
                                })}
                              </Tag>
                            )}
                          </span>
                          {event.locationLabel && (
                            <HoverTooltip content={event.locationLabel}>
                              <span className="contextEvents__eventLocation">{event.locationLabel}</span>
                            </HoverTooltip>
                          )}
                        </div>
                      </div>
                      <span className="contextEvents__colDates">
                        {`${formatShortDate(event.startDate)} – ${formatShortDate(event.endDate)}`}
                      </span>
                      <span className="contextEvents__colOta">{formatOta(event.ota)}</span>
                      <span className="contextEvents__colAudience">{formatCount(event.estimatedAudience)}</span>
                      <span className="contextEvents__colReasons">
                        <OtaReasons campaignOta={campaignOta} event={event} />
                      </span>
                      <EventActions
                        id={`contextEvents-actions-${event.id}`}
                        location={event.location}
                        onShowDetails={() => setSelectedEvent(event)}
                      />
                    </div>
                  </TaptapList.RowsList.Row>
                );
              })
            }
          </TaptapList.RowsList>
        </TaptapList>
      </div>
      <EventAudienceModal
        baselineOnTargetPct={baselineOnTargetPct}
        campaignOta={campaignOta}
        event={selectedEvent}
        id="contextEvents-audienceModal"
        onClose={() => setSelectedEvent(null)}
        onOpenOtaInfo={() => {
          setSelectedEvent(null);
          setIsOtaInfoOpen(true);
        }}
      />
      <OtaInfoModal id="contextEvents-otaInfoModal" isOpen={isOtaInfoOpen} onClose={() => setIsOtaInfoOpen(false)} />
    </section>
  );
}
